import React, { useContext, useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import LanguageContext from "../helper/LanguageContext";
import MaterialIcon from "./MaterialIcon";
import { stripHtmlToPlainText } from "../utils/conversationUtils";
import { ALL_ACCOUNTS_ID } from "./EmailFolderSidebar";
import EmailAssignModal from "./EmailAssignModal";
import ProjectTagActionModal from "./ProjectTagActionModal";
import { getProjectTagColors } from "../utils/projectTagColor";
import EmailEmptyState from "./EmailEmptyState";
import EmailSenderAvatar from "./EmailSenderAvatar";
import { extractSenderEmail } from "../helper/emailSenderLogo";
import { classifyMessageId, formatEmailSenderDisplay, assignModalSnapshot, isClassificationPending, getEmailListPreview } from "../helper/emailBodyFormat";
import { resolveStripeFlags } from "../utils/stripeFeature";
import { emailProjectIdOf } from "../helper/bookmarkTodo";
import "./EmailInbox.css";

const EMAIL_INBOX_SKELETON_ROWS = 9;

/** One-sentence preview — same logic as the email list summary. */
function buildEmailTodoSummary(email, summaryEnabled) {
  const preview = getEmailListPreview(email, !!summaryEnabled);
  if (preview) return preview;
  return stripHtmlToPlainText(email.subject || "").replace(/\s+/g, " ").trim() || "(No subject)";
}

/** Stable unique id for a list row. Trash/Spam/Sent payloads may not set id/_id,
 *  so fall back to messageId/providerMessageId to keep selection per-row. */
function getEmailId(email) {
  if (!email) return undefined;
  const raw = email.id ?? email._id ?? email.messageId ?? email.providerMessageId;
  if (raw == null || String(raw).trim() === "") return undefined;
  const id = String(raw).trim();
  const acc = email.__accountId || email.emailAccount || email.emailAccountId || "";
  // IMAP UIDs are folder-local; All Accounts would otherwise reuse the same React key.
  if (acc && /^\d+$/.test(id)) return `${acc}:${id}`;
  return id;
}

/** Same message if any id field matches (list row vs detail / GET payload may use different keys). */
function emailsShareIdentity(row, selected) {
  if (!row || !selected) return false;
  const collect = (e) => {
    const acc = String(e.__accountId || e.emailAccount || e.emailAccountId || "");
    return [e.id, e._id, e.messageId, e.providerMessageId, e.internetMessageId, classifyMessageId(e)]
      .filter((x) => x != null && String(x).trim() !== "")
      .map((x) => {
        const id = String(x);
        return acc && /^\d+$/.test(id) ? `${acc}:${id}` : id;
      });
  };
  const a = collect(row);
  const b = collect(selected);
  return a.some((id) => b.includes(id));
}

function EmailInboxLoadingSkeleton({ strings }) {
  return (
    <div className="email-inbox-loading" aria-busy="true" aria-live="polite">
      <div className="email-inbox-loading-bar-wrap" aria-hidden="true">
        <div className="email-inbox-loading-bar" />
      </div>
      <div className="email-inbox-loading-hint">
        <MaterialIcon
          icon="mail"
          size={18}
          color="#e066ed"
          className="email-inbox-loading-hint-icon"
        />
        <span>{strings.email_loading || "Loading emails..."}</span>
      </div>
      <div className="email-inbox-skeleton-scroll latestmessages_scrollbar loaded">
        {Array.from({ length: EMAIL_INBOX_SKELETON_ROWS }).map((_, i) => (
          <div
            key={i}
            className="email-inbox-skeleton-row email-inbox__skeleton-row"
            style={{ "--email-inbox-skeleton-delay": `${i * 42}ms` }}
          >
            <div className="email-inbox-skeleton-avatar" />
            <div className="email-inbox-skeleton-body">
              <div className="email-inbox-skeleton-line email-inbox-skeleton-line--short" />
              <div className="email-inbox-skeleton-line email-inbox-skeleton-line--med" />
              <div className="email-inbox-skeleton-line email-inbox-skeleton-line--full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const AUTO_SYNC_FOLDERS = ["INBOX", "STARRED"];

function EmailInbox({
  emailAccounts,
  activeEmailAccountId,
  onSelectAccount,
  emailMessages,
  emailLoading,
  onScroll,
  onSelectEmail,
  selectedEmailId,
  selectedEmail,
  paginationLoading,
  onCompose,
  hideTabs,
  onRefresh,
  onCreateTodoFromEmail,
  onRemoveTodoBookmark,
  onDeleteEmails,
  onMarkUnread,
  onMarkRead,
  onMoveEmails,
  onArchiveEmails,
  onMarkSpam,
  onToggleStar,
  customFolders,
  activeFolder,
  pendingClassifications,
  pendingClassificationCount,
  classificationBuildings,
  onClassifyEmail,
  onOpenProject,
  onSelectAllAcrossPages,
  onFolderSearch,
  isFolderSearching,
}) {
  const isManualSyncFolder = activeFolder && !AUTO_SYNC_FOLDERS.includes(activeFolder);
  const { strings, language } = useContext(LanguageContext);
  const history = useHistory();
  const profile = useSelector((state) => state.profile);
  const subscriptionState = useSelector((state) => state.subscription) || {};
  const summaryEnabled = useSelector((state) => !!state.user?.emailSummaryPreviewEnabled);
  const { stripeUiEnabled } = resolveStripeFlags(profile, subscriptionState);
  const hasActiveKjuupIoPlan = !stripeUiEnabled || !!subscriptionState.active;
  const hasUsedTrial = useSelector((state) => !!state.subscription?.hasUsedTrial);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [moveDropdownOpen, setMoveDropdownOpen] = useState(false);
  const moveDropdownRef = useRef(null);
  const listScrollRef = useRef(null);
  const lastScrolledSelectionRef = useRef(null);
  // Email whose "Associate to project" popup is currently open (null = closed).
  const [assignModalEmail, setAssignModalEmail] = useState(null);
  // Clicked project tag: { email, project } → shows the navigate/change chooser.
  const [tagAction, setTagAction] = useState(null);
  const [folderSearch, setFolderSearch] = useState("");
  const folderSearchDebounceRef = useRef(null);
  const lastSentFolderSearchRef = useRef("");

  // Map building/room id → theme color, so tags whose linkedProjects don't carry
  // themeColor still render in their project's specific color.
  const projectColorById = useMemo(() => {
    const m = new Map();
    (classificationBuildings || []).forEach((b) => {
      if (b && b.themeColor) m.set(String(b.id), b.themeColor);
    });
    return m;
  }, [classificationBuildings]);
  const resolveProjectColor = (p) => (p && p.themeColor) || projectColorById.get(String(p?.id)) || null;

  useEffect(() => {
    setFolderSearch("");
    lastSentFolderSearchRef.current = "";
    if (folderSearchDebounceRef.current) {
      clearTimeout(folderSearchDebounceRef.current);
    }
  }, [activeFolder, activeEmailAccountId]);

  useEffect(() => {
    return () => {
      if (folderSearchDebounceRef.current) {
        clearTimeout(folderSearchDebounceRef.current);
      }
    };
  }, []);

  const emitFolderSearch = (value) => {
    if (!onFolderSearch) return;
    const trimmed = String(value || "").trim();
    const toSend = trimmed.length >= 2 ? trimmed : "";
    if (toSend === lastSentFolderSearchRef.current) return;
    lastSentFolderSearchRef.current = toSend;
    onFolderSearch(toSend);
  };

  const handleFolderSearchChange = (value) => {
    setFolderSearch(value);
    if (folderSearchDebounceRef.current) clearTimeout(folderSearchDebounceRef.current);
    const trimmed = String(value || "").trim();
    // Restore the folder list immediately when the query drops below the
    // 2-char search threshold so clearing search does not wait on debounce
    // or a full Sent/IMAP refetch.
    if (trimmed.length < 2) {
      emitFolderSearch(value);
      return;
    }
    folderSearchDebounceRef.current = setTimeout(() => emitFolderSearch(value), 300);
  };

  const clearFolderSearch = () => {
    setFolderSearch("");
    if (folderSearchDebounceRef.current) clearTimeout(folderSearchDebounceRef.current);
    lastSentFolderSearchRef.current = "";
    if (onFolderSearch) onFolderSearch("");
  };

  useEffect(() => {
    if (!moveDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (moveDropdownRef.current && !moveDropdownRef.current.contains(e.target)) {
        setMoveDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moveDropdownOpen]);

  const handleMoveSelected = async (targetFolder) => {
    if (selectedIds.size === 0 || !onMoveEmails) return;
    setMoveDropdownOpen(false);
    await onMoveEmails([...selectedIds], targetFolder);
    exitSelectMode();
  };

  const handleArchiveSelected = async () => {
    if (selectedIds.size === 0 || !onArchiveEmails) return;
    await onArchiveEmails([...selectedIds]);
    exitSelectMode();
  };

  const handleMarkSpamSelected = async () => {
    if (selectedIds.size === 0 || !onMarkSpam) return;
    await onMarkSpam([...selectedIds]);
    exitSelectMode();
  };

  const handleMarkReadSelected = async () => {
    if (selectedIds.size === 0 || !onMarkRead) return;
    await onMarkRead([...selectedIds]);
    exitSelectMode();
  };

  // Auto-scroll the selected email into view, but ONLY when the user's selection
  // actually changes (e.g. clicked a new email). We must NOT re-scroll on every
  // emailMessages change (socket updates / refresh), or that would yank the
  // scroll position back to the selected message while the user is browsing
  // older messages.
  const currentSelectionKey = selectedEmail
    ? String(
        selectedEmail.id ||
        selectedEmail._id ||
        selectedEmail.messageId ||
        selectedEmail.providerMessageId ||
        ""
      )
    : selectedEmailId != null
      ? String(selectedEmailId)
      : "";

  useEffect(() => {
    if (selectMode || emailLoading) return;
    if (!currentSelectionKey) {
      lastScrolledSelectionRef.current = null;
      return;
    }
    if (lastScrolledSelectionRef.current === currentSelectionKey) return;
    if (emailMessages.length === 0) return;
    const container = listScrollRef.current;
    if (!container) return;
    const id = requestAnimationFrame(() => {
      const row = container.querySelector(".email-item.email-item--selected");
      if (row) {
        row.scrollIntoView({ block: "center", behavior: "smooth" });
        lastScrolledSelectionRef.current = currentSelectionKey;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [currentSelectionKey, emailMessages, emailLoading, selectMode]);

  // Preserve the user's visual scroll position across silent syncs.
  //
  // Background: every 60s (see LatestMessages auto-sync poll) and on every
  // websocket-driven `mergeIncomingMessages` callback, new emails are
  // PREPENDED to `emailMessages` (sorted by date DESC). The browser keeps
  // `scrollTop` numerically constant across re-renders, but visually the
  // user's content gets pushed down by the height of the items inserted
  // above their viewport – which feels exactly like "the scroll jumped"
  // even though the user never touched the wheel.
  //
  // We fix that by compensating `scrollTop` by the height delta whenever
  // the list's first email id changes AND the user isn't already at the
  // top (at the top, the new emails SHOULD scroll into view naturally).
  // `useLayoutEffect` runs after DOM updates but before paint, so the
  // adjustment happens in the same frame as the prepend – the user never
  // sees the jump.
  const prevFirstEmailIdRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const firstEmailId = useMemo(() => {
    const first = emailMessages?.[0];
    if (!first) return null;
    return String(
      first.id || first._id || first.messageId || first.providerMessageId || ""
    );
  }, [emailMessages]);

  useLayoutEffect(() => {
    const container = listScrollRef.current;
    if (!container) {
      prevFirstEmailIdRef.current = firstEmailId;
      prevScrollHeightRef.current = 0;
      return;
    }
    const prevFirstId = prevFirstEmailIdRef.current;
    const prevHeight = prevScrollHeightRef.current;
    const newHeight = container.scrollHeight;
    const currentScrollTop = container.scrollTop;

    const firstChanged =
      prevFirstId !== null && firstEmailId !== null && prevFirstId !== firstEmailId;
    const heightGrew = prevHeight > 0 && newHeight > prevHeight;
    const userIsBrowsing = currentScrollTop > 4;

    if (firstChanged && heightGrew && userIsBrowsing) {
      const delta = newHeight - prevHeight;
      container.scrollTop = currentScrollTop + delta;
    }

    prevFirstEmailIdRef.current = firstEmailId;
    prevScrollHeightRef.current = newHeight;
  }, [firstEmailId, emailMessages]);

  const toggleSelectId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = async () => {
    const loadedIds = emailMessages.map((e) => getEmailId(e)).filter(Boolean);
    // Everything (at least all loaded rows) already selected → clear.
    if (loadedIds.length > 0 && selectedIds.size >= loadedIds.length) {
      setSelectedIds(new Set());
      return;
    }
    // Prefer selecting EVERY matching e-mail across all pages (not just the
    // loaded first page) when the parent can fetch the full id set. We always
    // keep the loaded rows too, so nothing already on screen is dropped.
    if (onSelectAllAcrossPages) {
      setSelectAllBusy(true);
      try {
        const ids = await onSelectAllAcrossPages();
        const merged = new Set(loadedIds);
        (ids || []).forEach((id) => {
          if (id != null && String(id).trim() !== "") merged.add(id);
        });
        setSelectedIds(merged);
      } catch (e) {
        setSelectedIds(new Set(loadedIds));
      } finally {
        setSelectAllBusy(false);
      }
      return;
    }
    setSelectedIds(new Set(loadedIds));
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleMarkUnread = async () => {
    if (selectedIds.size === 0 || !onMarkUnread) return;
    await onMarkUnread([...selectedIds]);
    exitSelectMode();
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0 || !onDeleteEmails) return;
    const ids = [...selectedIds];
    setDeleting(true);
    try {
      // The parent renders a single full-screen progress overlay (counter +
      // progress bar) while it deletes, so we just delegate here.
      await onDeleteEmails(ids);
    } finally {
      setDeleting(false);
      exitSelectMode();
    }
  };

  const LOCALE_MAP = { de: "de-DE", en: "en-GB", fr: "fr-FR", it: "it-IT", es: "es-ES", pt: "pt-PT", nl: "nl-NL", pl: "pl-PL", ru: "ru-RU", ja: "ja-JP", ko: "ko-KR", zh: "zh-CN", ar: "ar-SA", tr: "tr-TR", sv: "sv-SE", da: "da-DK", no: "nb-NO", fi: "fi-FI", cs: "cs-CZ", hu: "hu-HU", ro: "ro-RO", bg: "bg-BG", hr: "hr-HR", sl: "sl-SI", el: "el-GR", id: "id-ID", ms: "ms-MY", vi: "vi-VN" };
  const locale = LOCALE_MAP[language?.toLowerCase()] || LOCALE_MAP.en;

  const getEmailDateGroupKey = (dateStr) => {
    if (!dateStr) return "earlier";
    const d = new Date(dateStr);
    if (isNaN(d)) return "earlier";
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const sevenDaysAgo = new Date(startOfToday);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    if (d >= startOfToday) return "today";
    if (d >= startOfYesterday) return "yesterday";
    if (d >= sevenDaysAgo) return "thisWeek";
    return "earlier";
  };

  const getGroupLabel = (key) => {
    if (key === "today") return strings.email_group_today || "Today";
    if (key === "yesterday") return strings.email_group_yesterday || "Yesterday";
    if (key === "thisWeek") return strings.email_group_this_week || "This week";
    if (key === "earlier") return strings.email_group_earlier || "Earlier";
    return key;
  };

  // Map of message-id -> pending classification item. We index every id variant
  // (id/_id/messageId/providerMessageId) because the pending queue and the inbox
  // list rows don't always key off the same field.
  const pendingClassMap = useMemo(() => {
    const map = new Map();
    (pendingClassifications || []).forEach((p) => {
      [p?.id, p?._id, p?.messageId, p?.providerMessageId].forEach((v) => {
        if (v != null && String(v).trim() !== "") map.set(String(v), p);
      });
    });
    return map;
  }, [pendingClassifications]);

  // Which pending item (if any) matches this inbox row, trying every id variant.
  const getPendingForEmail = (email) => {
    for (const v of [email?.id, email?._id, email?.messageId, email?.providerMessageId]) {
      if (v != null && String(v).trim() !== "") {
        const hit = pendingClassMap.get(String(v));
        if (hit) return hit;
      }
    }
    return null;
  };

  const groupedEmails = useMemo(() => {
    if (!emailMessages || emailMessages.length === 0) return [];
    const groups = new Map();
    let flatIdx = 0;
    for (const email of emailMessages) {
      const key = getEmailDateGroupKey(email.date || email.receivedAt);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ ...email, __flatIndex: flatIdx++ });
    }
    return Array.from(groups, ([key, emails]) => ({ key, label: getGroupLabel(key), emails }));
  }, [emailMessages, strings, locale]);

  if (!hasActiveKjuupIoPlan) {
    return (
      <EmailEmptyState
        variant={hasUsedTrial ? "trialEnded" : "noPlan"}
        onPrimary={() => history.push("/subscription")}
      />
    );
  }

  if (!emailAccounts || emailAccounts.length === 0) {
    return (
      <EmailEmptyState
        variant="noAccounts"
        onPrimary={() => history.push("/profile-setting", { openSection: "email-accounts" })}
      />
    );
  }

  const formatEmailDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return strings.yesterday || "Yesterday";
    }
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      return date.toLocaleDateString(locale, { weekday: "long" });
    }
    return date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "2-digit" });
  };

  const getSenderName = (email) => {
    const display = formatEmailSenderDisplay(email);
    if (display) {
      const nameMatch = display.match(/^(.+?)\s*<.*>$/);
      return nameMatch ? nameMatch[1].trim() : display;
    }
    const name = email.fromAddress?.name || email.fromName;
    if (name) return name;
    const addr = email.fromAddress?.email || email.from;
    if (addr) {
      const nameMatch = addr.match(/^(.+?)\s*<.*>$/);
      if (nameMatch) return nameMatch[1].trim();
      const extracted = addr.match(/<(.+?)>/);
      return extracted ? extracted[1] : addr;
    }
    return "";
  };

  return (
    <div className="email-inbox">
      {!hideTabs && emailAccounts.length > 0 && (
        <div className="email-inbox__tabs-row">
          <div className="email-account-tabs email-inbox__account-tabs">
            {emailAccounts.length > 1 && (
              <button
                key={ALL_ACCOUNTS_ID}
                className={`kj-tab${activeEmailAccountId === ALL_ACCOUNTS_ID ? " is-active" : ""}`}
                onClick={() => onSelectAccount(ALL_ACCOUNTS_ID)}
              >
                <span className="email-account-tab-icon">
                  <MaterialIcon icon="all_inbox" size={14} />
                </span>
                <span className="email-account-tab-label">
                  {strings.email_all_accounts || "All Accounts"}
                </span>
              </button>
            )}
            {emailAccounts.map((account) => (
            <button
              key={account.id || account._id}
              className={`kj-tab${activeEmailAccountId === (account.id || account._id) ? " is-active" : ""}`}
              onClick={() => onSelectAccount(account.id || account._id)}
            >
              <span className="email-account-tab-icon">
                <MaterialIcon icon="mail" size={14} />
              </span>
              <span className="email-account-tab-label">
                {account.email || account.name || account.provider}
              </span>
            </button>
          ))}
          </div>
          {onCompose && (
            <button
              className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill email-compose-new-btn"
              onClick={onCompose}
              title={strings.new_email || "New E-Mail"}
            >
              <MaterialIcon icon="edit" size={14} />
              <span className="email-compose-new-label">{strings.new_email || "New E-Mail"}</span>
            </button>
          )}
        </div>
      )}

      {(onRefresh || onCompose || onDeleteEmails) && (
        <div className="email-inbox-toolbar">
          {selectMode ? (
            <>
              <button
                className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                onClick={exitSelectMode}
                title={strings.cancel || "Cancel"}
              >
                <MaterialIcon icon="close" size={14} />
              </button>
              {emailMessages.length > 0 && (() => {
                // "Select all" toggle. Selects every matching e-mail across
                // ALL pages (via onSelectAllAcrossPages), not just the loaded
                // first page; when everything is already selected it deselects
                // all. Pairs naturally with the "Unread only" filter — toggle
                // it on, click Select all, then apply a batch action.
                const allSelected = selectedIds.size >= emailMessages.length && emailMessages.length > 0;
                return (
                  <button
                    data-testid="multiSelectSelectAll"
                    className={`kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill${allSelected ? " active" : ""}`}
                    onClick={toggleSelectAll}
                    disabled={selectAllBusy}
                    title={
                      allSelected
                        ? (strings.email_deselect_all || "Deselect all")
                        : (strings.email_select_all || "Select all")
                    }
                    aria-pressed={allSelected}
                  >
                    <MaterialIcon
                      icon={selectAllBusy ? "autorenew" : (allSelected ? "deselect" : "select_all")}
                      size={14}
                      className={selectAllBusy ? "email-refresh-spin" : ""}
                    />
                    <span>
                      {allSelected
                        ? (strings.email_deselect_all || "Deselect all")
                        : (strings.email_select_all || "Select all")}
                    </span>
                  </button>
                );
              })()}
              <span className="email-multiselect-count">({selectedIds.size})</span>
              {onMarkRead && (
                <button
                  data-testid="multiSelectMarkRead"
                  className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                  onClick={handleMarkReadSelected}
                  disabled={selectedIds.size === 0}
                  title={strings.mark_read || "Mark read"}
                >
                  <MaterialIcon icon="drafts" size={14} />
                </button>
              )}
              {onMarkUnread && (
                <button
                  data-testid="multiSelectMarkUnread"
                  className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                  onClick={handleMarkUnread}
                  disabled={selectedIds.size === 0}
                  title={strings.mark_unread || "Mark unread"}
                >
                  <MaterialIcon icon="mark_email_unread" size={14} />
                </button>
              )}
              {onArchiveEmails && activeFolder !== "ARCHIVE" && activeFolder !== "TRASH" && (
                <button
                  data-testid="multiSelectArchive"
                  className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                  onClick={handleArchiveSelected}
                  disabled={selectedIds.size === 0}
                  title={strings.email_archive || "Archive"}
                >
                  <MaterialIcon icon="archive" size={14} />
                </button>
              )}
              {onMoveEmails && (
                <div className="email-move-dropdown-wrap" ref={moveDropdownRef}>
                  <button
                    data-testid="multiSelectMove"
                    className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                    onClick={() => setMoveDropdownOpen((v) => !v)}
                    disabled={selectedIds.size === 0}
                    title={strings.email_move_to || "Move to"}
                  >
                    <MaterialIcon icon="drive_file_move" size={14} />
                  </button>
                  {moveDropdownOpen && (
                    <div className="email-move-dropdown">
                      {activeFolder !== "INBOX" && (
                        <div className="email-move-dropdown-item" data-testid="moveSheetRow-INBOX" onClick={() => handleMoveSelected("INBOX")}>
                          <MaterialIcon icon="inbox" size={16} />
                          <span>{strings.email_folder_inbox || "Inbox"}</span>
                        </div>
                      )}
                      {activeFolder !== "ARCHIVE" && (
                        <div className="email-move-dropdown-item" data-testid="moveSheetRow-ARCHIVE" onClick={() => handleMoveSelected("ARCHIVE")}>
                          <MaterialIcon icon="archive" size={16} />
                          <span>{strings.email_folder_archive || "Archive"}</span>
                        </div>
                      )}
                      {activeFolder !== "SPAM" && (
                        <div className="email-move-dropdown-item" data-testid="moveSheetRow-SPAM" onClick={() => handleMoveSelected("SPAM")}>
                          <MaterialIcon icon="report" size={16} />
                          <span>{strings.email_folder_spam || "Spam"}</span>
                        </div>
                      )}
                      {activeFolder !== "TRASH" && (
                        <div className="email-move-dropdown-item" data-testid="moveSheetRow-TRASH" onClick={() => handleMoveSelected("TRASH")}>
                          <MaterialIcon icon="delete" size={16} />
                          <span>{strings.email_folder_deleted || "Trash"}</span>
                        </div>
                      )}
                      {(customFolders || []).length > 0 && <div className="email-move-dropdown-divider" />}
                      {(customFolders || []).map((f) => (
                        <div
                          key={f.id}
                          className="email-move-dropdown-item"
                          data-testid={`moveSheetRow-${(f.name || '').toUpperCase()}`}
                          onClick={() => handleMoveSelected(f.providerFolderId)}
                        >
                          <MaterialIcon icon="folder" size={16} />
                          <span>{f.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {onMarkSpam && activeFolder !== "SPAM" && (
                <button
                  data-testid="multiSelectMarkSpam"
                  className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                  onClick={handleMarkSpamSelected}
                  disabled={selectedIds.size === 0}
                  title={strings.email_mark_spam || "Mark as spam"}
                >
                  <MaterialIcon icon="report" size={14} />
                </button>
              )}
              <button
                className="kj-btn kj-btn--outline kj-btn--danger kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0 || deleting}
                title={strings.delete || "Delete"}
              >
                <MaterialIcon icon={deleting ? "refresh" : "delete"} size={14} className={deleting ? "email-refresh-spin" : ""} />
                <span>{deleting ? (strings.deleting || "...") : `(${selectedIds.size})`}</span>
              </button>
            </>
          ) : (
            <>
              {onRefresh && (
                <button
                  className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                  onClick={onRefresh}
                  disabled={emailLoading}
                  title={strings.email_refresh || "Refresh"}
                >
                  <MaterialIcon icon="refresh" size={14} className={emailLoading ? "email-refresh-spin" : ""} />
                  <span>{strings.email_refresh || "Refresh"}</span>
                </button>
              )}
              {onDeleteEmails && emailMessages.length > 0 && (
                <button
                  className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
                  onClick={() => setSelectMode(true)}
                  title={strings.email_select || "Select"}
                >
                  <MaterialIcon icon="checklist" size={14} />
                  <span>{strings.email_select || "Select"}</span>
                </button>
              )}
              {onFolderSearch && (
                <div className="email-folder-search">
                  <MaterialIcon icon="search" size={14} color="#e066ed" />
                  <input
                    type="text"
                    className="email-folder-search-input"
                    data-testid="emailFolderSearch"
                    value={folderSearch}
                    onChange={(e) => handleFolderSearchChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (folderSearchDebounceRef.current) {
                          clearTimeout(folderSearchDebounceRef.current);
                        }
                        emitFolderSearch(folderSearch);
                      } else if (e.key === "Escape") {
                        clearFolderSearch();
                      }
                    }}
                    placeholder={strings.email_search_placeholder || "Search in this folder"}
                    aria-label={strings.email_search_placeholder || "Search in this folder"}
                  />
                  {isFolderSearching ? (
                    <MaterialIcon icon="refresh" size={14} className="email-refresh-spin" />
                  ) : folderSearch ? (
                    <button
                      type="button"
                      className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                      onClick={clearFolderSearch}
                      title={strings.cancel || "Clear"}
                      aria-label={strings.cancel || "Clear"}
                    >
                      <MaterialIcon icon="close" size={14} />
                    </button>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isManualSyncFolder && !emailLoading && emailMessages.length === 0 && !folderSearch && !isFolderSearching && (
        <div className="email-manual-sync-banner">
          <MaterialIcon icon="sync" size={20} color="#e066ed" />
          <span>{strings.email_folder_not_synced || "This folder is not automatically synced."}</span>
          <button
            className="kj-btn kj-btn--primary kj-btn--email kj-btn--sm email-manual-sync-btn"
            onClick={onRefresh}
            disabled={emailLoading}
          >
            <MaterialIcon icon="refresh" size={14} className={emailLoading ? "email-refresh-spin" : ""} />
            <span>{strings.email_sync_folder || "Sync now"}</span>
          </button>
        </div>
      )}

      {(emailLoading || isFolderSearching) && emailMessages.length === 0 ? (
        <EmailInboxLoadingSkeleton strings={strings} />
      ) : emailMessages.length === 0 ? (
        <div className="email-empty-state">
          <MaterialIcon icon={folderSearch ? "search" : "inbox"} size={48} color="#737373" className="email-inbox__empty-icon" />
          <h4 className="header-margin-gray">{strings.email_no_messages}</h4>
        </div>
      ) : (
        <div
          ref={listScrollRef}
          className="scroll latestmessages_scrollbar loaded email-inbox-list-enter email-inbox__list"
          onScroll={onScroll}
        >
          {groupedEmails.map((group) => (
            <div key={group.key}>
              <div className="email-date-separator" data-testid={`emailGroupHeader-${group.key}`}>{group.label}</div>
              {group.emails.map((email) => {
                const emailId = getEmailId(email);
                const isSelected = selectedEmail
                  ? emailsShareIdentity(email, selectedEmail)
                  : selectedEmailId != null && String(selectedEmailId) === String(emailId);
                const isUnread = !email.isRead && email.isRead !== undefined;
                const senderName = getSenderName(email);
                const preview = getEmailListPreview(email, summaryEnabled);

                return (
                  <div
                    key={emailId}
                    className={`email-item ${isUnread ? "email-item--unread" : ""} ${isSelected ? "email-item--selected" : ""}`}
                    onClick={() => selectMode ? toggleSelectId(emailId) : onSelectEmail(email)}
                  >
                    {selectMode ? (
                      <div className="email-item-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(emailId)}
                          readOnly
                          className="email-select-checkbox"
                        />
                      </div>
                    ) : null}
                    <div className="email-item-avatar">
                      <EmailSenderAvatar
                        className="email-item-avatar-circle"
                        email={extractSenderEmail(email)}
                        name={senderName}
                        size={30}
                      />
                    </div>
                    <div className="email-item-content">
                      <div className="email-item-header">
                        <div className="email-item-sender-line">
                          <span className="email-item-sender">
                            {isUnread && <span className="email-item-unread-dot" aria-hidden="true" />}
                            {senderName}
                          </span>
                          {(() => {
                            if (selectMode) return null;
                            if (email.systemTag === "daily_briefing") {
                              return (
                                <span className="email-item-sender-tags">
                                  <span className="email-item-briefing-tag" title={strings.email_tag_daily_briefing || "Daily report"}>
                                    <MaterialIcon icon="newspaper" size={11} color="#ffffff" />
                                    <span>{strings.email_tag_daily_briefing || "Daily report"}</span>
                                  </span>
                                </span>
                              );
                            }
                            if (isClassificationPending(email)) return null;
                            const filed = Array.isArray(email.linkedProjects) && email.linkedProjects.length > 0;
                            if (filed) {
                              // Assigned: show the project tag(s) next to the sender.
                              return (
                                <span className="email-item-sender-tags">
                                  {email.linkedProjects.map((p) => (
                                    <span
                                      key={p.id}
                                      role="button"
                                      tabIndex={0}
                                      className="email-item-project-tag email-inbox__tag"
                                      title={p.name}
                                      style={(() => { const tc = getProjectTagColors(resolveProjectColor(p)); return { "--email-tag-bg": tc.bg, "--email-tag-fg": tc.fg }; })()}
                                      onClick={(e) => { e.stopPropagation(); setTagAction({ email, project: p }); }}
                                    >
                                      <MaterialIcon icon="folder" size={11} color={getProjectTagColors(resolveProjectColor(p)).fg} />
                                      <span>{p.name}</span>
                                    </span>
                                  ))}
                                </span>
                              );
                            }
                            if (!onClassifyEmail) return null;
                            // Unclassified: missing-project chip that opens the assign popup.
                            // "Mark as spam" lives only on the All tab (LatestMessages), not here.
                            return (
                              <span className="email-item-sender-tags">
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="email-item-associate-btn"
                                  title={strings.email_classify_associate || "Associate to project"}
                                  onClick={(e) => { e.stopPropagation(); setAssignModalEmail(assignModalSnapshot(email)); }}
                                >
                                  <span className="email-item-associate-missing-dot" aria-hidden="true" />
                                  <MaterialIcon icon="folder_off" size={13} />
                                  <span>{strings.email_classify_missing || "Unassigned"}</span>
                                </span>
                              </span>
                            );
                          })()}
                        </div>
                        <span className="email-item-header-right">
                          <span className="email-item-date">{formatEmailDate(email.date || email.receivedAt)}</span>
                          {false && onToggleStar && !selectMode && (
                            <button
                              type="button"
                              className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email email-item-star-btn"
                              data-testid={`emailRowStar-${email.__flatIndex}`}
                              title={email.isStarred ? (strings.email_unstar || "Unstar") : (strings.email_star || "Star")}
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleStar(emailId, !email.isStarred);
                              }}
                            >
                              <MaterialIcon
                                icon={email.isStarred ? "star" : "star_border"}
                                size={20}
                                color={email.isStarred ? "#e066ed" : undefined}
                                fill={email.isStarred ? 1 : 0}
                                weight={300}
                                grade={0}
                              />
                            </button>
                          )}
                          {onMoveEmails && activeFolder === "TRASH" && !selectMode && (
                            <button
                              type="button"
                              data-testid="rowRestoreToInbox"
                              className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email"
                              title={strings.email_move_to_inbox || "Move to Inbox"}
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveEmails([emailId], "INBOX");
                              }}
                            >
                              <MaterialIcon
                                icon="move_to_inbox"
                                size={24}
                                fill={0}
                                weight={300}
                                grade={0}
                              />
                            </button>
                          )}
                          {onDeleteEmails && !selectMode && (
                            <button
                              type="button"
                              className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger email-item-delete-btn"
                              title={strings.delete || "Delete"}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteEmails([emailId]);
                              }}
                            >
                              <MaterialIcon
                                icon="delete"
                                size={24}
                                fill={0}
                                weight={300}
                                grade={0}
                              />
                            </button>
                          )}
                          {/* "Mark as task" only for an email filed under a project (removing stays possible). */}
                          {onCreateTodoFromEmail && !selectMode && (email.isTodoBookmarked || !!emailProjectIdOf(email)) && (
                            <button
                              type="button"
                              className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email email-item-todo-btn"
                              title={email.isTodoBookmarked ? strings.unmark_task || "Remove from tasks" : strings.mark_as_task || "Mark as task"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (email.isTodoBookmarked) {
                                  if (onRemoveTodoBookmark) onRemoveTodoBookmark(emailId);
                                } else {
                                  // The email itself goes along: its project is the task's project.
                                  onCreateTodoFromEmail(
                                    emailId,
                                    email.date || email.receivedAt,
                                    buildEmailTodoSummary(email, summaryEnabled),
                                    email
                                  );
                                }
                              }}
                            >
                              <MaterialIcon
                                icon={email.isTodoBookmarked ? "task_alt" : "add_task"}
                                size={24}
                                color={email.isTodoBookmarked ? "#e066ed" : undefined}
                                fill={email.isTodoBookmarked ? 1 : 0}
                                weight={300}
                                grade={0}
                                className="bookmark-action-icon"
                              />
                            </button>
                          )}
                        </span>
                      </div>
                      <div className="email-item-subject-row">
                        <span className="email-item-subject">{email.subject || (strings.email_no_subject || "(No subject)")}</span>
                        {email.hasAttachments && <MaterialIcon icon="attach_file" size={13} className="email-item-attachment-icon" />}
                      </div>
                      {preview && (
                        <div className="email-item-preview">{preview}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {paginationLoading && (
            <div className="email-inbox__pager">
              <MaterialIcon icon="autorenew" size={24} color="#e066ed" className="email-refresh-spin" />
            </div>
          )}
        </div>
      )}

      {assignModalEmail && onClassifyEmail && (
        <EmailAssignModal
          email={assignModalEmail}
          proposedName={getPendingForEmail(assignModalEmail)?.proposedBuilding?.name}
          buildings={classificationBuildings}
          onClassify={onClassifyEmail}
          onClose={() => setAssignModalEmail(null)}
          strings={strings}
        />
      )}

      {tagAction && (
        <ProjectTagActionModal
          project={tagAction.project}
          projectColor={resolveProjectColor(tagAction.project)}
          onNavigate={() => {
            if (onOpenProject) onOpenProject(tagAction.project, getEmailId(tagAction.email));
            setTagAction(null);
          }}
          onChange={() => {
            const em = tagAction.email;
            setTagAction(null);
            if (onClassifyEmail) setAssignModalEmail(assignModalSnapshot(em));
          }}
          onRemove={() => {
            const em = tagAction.email;
            const bId = tagAction.project?.id;
            setTagAction(null);
            if (onClassifyEmail) onClassifyEmail(classifyMessageId(em) || getEmailId(em), "unlink", "", bId);
          }}
          onClose={() => setTagAction(null)}
          strings={strings}
        />
      )}
    </div>
  );
}

export default EmailInbox;
